import { Controller, ActionEvent } from "@hotwired/stimulus";
import { Loader } from "@googlemaps/js-api-loader";
import { decode } from "@googlemaps/polyline-codec";

import { adminMapPlacePath } from "src/routes";
import { reducedClutterGoogleMapStyle } from "src/admin/map_styles";

interface SuburbListing {
  id: number;
  name: string;
  map_color_index?: number;
  map_place_id?: number;
  boundaries?: string[];
}

export default class extends Controller {
  readonly UNSERVICED_FILL_COLOR = "rgb(0, 0, 0)";
  readonly SELECTED_UNSERVICED_FILL_COLOR = "rgb(100, 100, 100)";
  readonly NORMAL_FILL_COLORS = ["rgb(15, 130, 242)", "rgb(34, 171, 36)", "rgb(253, 140, 0)", "rgb(235, 71, 38)", "rgb(109, 59, 191)", "rgb(205, 14, 102)", "rgb(0, 158, 166)"]

  static targets = ["map"];

  declare mapTarget: HTMLElement;
  declare hasMapTarget: boolean;

  // we can't initialize any Maps objects until it's loaded, and we need to run the Loader to do that
  map?: google.maps.Map;

  loadDebounceTimer?: number;

  suburbPolygons = new Map<number, google.maps.Polygon>();

  async connect(): Promise<void> {
    const mapsKey = this.data.get("maps-key");
    if (!this.hasMapTarget || !mapsKey) return;
    await this.loadMaps(mapsKey);
    this.createMap();

    // If a bootstrapping path is configured - for example, the suburbs-for-area path - load from it
    const path = this.data.get("path");
    if (path) await this.loadDataFrom(path);

    this.setInitialBounds();
  }

  disconnect(): void {
    this.suburbPolygons.forEach((suburb) => suburb.setMap(null));
    this.suburbPolygons.clear();

    this.map = undefined;
  }

  protected async loadMaps(mapsApiKey: string): Promise<void> {
    const loader = new Loader({ apiKey: mapsApiKey, region: "NZ", language: "en" });
    await loader.load();
    await google.maps.importLibrary("places");
  }

  protected createMap(): void {
    this.map = new google.maps.Map(this.mapTarget, {
      center: { lat: -41.2529389, lng: 174.6842181 },
      zoom: 6,
      styles: reducedClutterGoogleMapStyle,
    });

    google.maps.event.addListener(this.map, "bounds_changed", () => {
      // we get repeated events if the user is panning around, which queue up and cause both high load and
      // high latency for the latest bounds. debounce so we don't make more than one request per 100ms.
      this.loadDebounceTimer ??= window.setTimeout(() => {
        this.loadDebounceTimer = undefined;
        this.boundsChanged();
      }, 100);
    });
  }

  protected boundsChanged(): void {
    // Overridden in subclasses that load scrolled-into-view data on demand
  }

  protected async loadDataFrom(path: string): Promise<void> {
    const response = await fetch(path, { credentials: 'same-origin' });
    const json = await response.json();

    for (const suburb of json.suburbs) {
      if (!this.suburbPolygons.has(suburb.id)) {
        this.suburbPolygons.set(suburb.id, this.renderSuburb(suburb));
      }
    }
  }

  protected addBoundsToPath(path: string, bounds: google.maps.LatLngBounds): string {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    return `${path}?north=${ne.lat()}&east=${ne.lng()}&south=${sw.lat()}&west=${sw.lng()}`;
  }

  protected setInitialBounds(): void {
    const bounds = new google.maps.LatLngBounds();

    for (const polygon of this.suburbPolygons.values()) {
      polygon.getPaths().forEach((path) => path.forEach((latlng) => bounds.extend(latlng)));
    }

    this.map?.fitBounds(bounds, 0);
  }

  protected decodePolyline(polyline: string): google.maps.LatLng[] {
    return decode(polyline).map((tuple) => new google.maps.LatLng(tuple[0], tuple[1]));
  }

  protected suburbFillColor(suburb: SuburbListing): string {
    const map_color_index = (suburb?.map_color_index ?? suburb.id) % this.NORMAL_FILL_COLORS.length;
    return this.NORMAL_FILL_COLORS[map_color_index];
  }

  protected renderSuburb(suburb: SuburbListing): google.maps.Polygon {
    const paths = suburb.boundaries?.map((polyline) => this.decodePolyline(polyline));
    const polygon = new google.maps.Polygon({
      map: this.map,
      paths: paths,
      fillColor: this.suburbFillColor(suburb),
      fillOpacity: 0.4,
      strokeOpacity: 0.2,
      strokeColor: "rgba(50, 50, 50)",
      strokeWeight: 1,
      clickable: !!suburb.map_place_id,
    });
    if (suburb.map_place_id) {
      polygon.addListener("click", () => this.suburbClicked(suburb));
    }
    return polygon;
  }

  highlight(event: ActionEvent): void {
    const polygon = this.suburbPolygons.get(event.params["suburbId"]);
    if (polygon) polygon.setOptions({ fillOpacity: 0.6 });
  }

  unhighlight(event: ActionEvent): void {
    const polygon = this.suburbPolygons.get(event.params["suburbId"]);
    if (polygon) polygon.setOptions({ fillOpacity: 0.4 });
  }

  private suburbClicked(suburb: SuburbListing): void {
    if (suburb.map_place_id) {
      window.location.href = adminMapPlacePath(suburb.map_place_id);
    }
  }
}

export { SuburbListing };
